import { LiveFrame } from './LiveFrame';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  X, 
  RefreshCw, 
  Activity,  
  Car, 
  User, 
  ArrowUpRight, 
  ArrowDownRight, 
  ShieldCheck,
  Zap,
  TrendingUp,
  Table as TableIcon,
  VideoOff,
  Database
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import baseapi, { API_BASE } from '../../baseapi';
import { LineEditor, type CountLine } from './LineEditor';
import { PencilRuler, Scan } from 'lucide-react';
import { ZoneEditor } from './ZoneEditor';

export interface RecentDetection {
  ts: number;
  line_id: string;
  direction: 'toward' | 'away' | string;
  cls: string;
  track_id: number;
}

export interface CameraStatusData {
  state: string;
  detail: string;
  source: string;
  frame_age_s: number;
  proc_fps: number;
  decode_fps: number;
  decoder: string;
  frames_received: number;
  frames_processed: number;
  frame_size: [number, number];
  tracks: number;
  recent: RecentDetection[];
  /** this camera's counts since the daily reset, counted like /summary (backend, 12 Oct 2026) */
  live?: {
    since: number; toward: number; away: number; veh_toward: number; veh_away: number;
    lines?: Record<string, { toward: { m1: number; m5: number; m15: number; total: number }; away: { m1: number; m5: number; m15: number; total: number } }>;
  };
}

export interface SeriesPoint {
  t: number;
  toward: number;
  away: number;
  veh_toward: number;
  veh_away: number;
  /** crowd in view: average / peak people tracked in the frame during this bucket */
  crowd?: number;
  crowd_max?: number;
  /** people per count line in this bucket (queue-lane cameras: Q1..Q5) */
  lines?: Record<string, { toward: number; away: number }>;
}

/** Queue-lane cameras name their count lines Q1, Q2, ... (one line across each lane). */
const QUEUE_LINE_RE = /^Q\d+$/;
const QUEUE_COLORS = ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#a855f7', '#14b8a6', '#ec4899', '#84cc16'];

export interface CheckpointSeriesData {
  since: number;
  points: SeriesPoint[];
}

interface CameraStreamModalProps {
  checkpoint: {
    id: string;
    name: string;
    kind?: string;
    hub?: string;
    coverage?: string;
    cameras?: Array<{
      id: string;
      state: string;
      detail?: string;
      source?: string;
      proc_fps?: number;
      decode_fps?: number;
      decoder?: string;
      frame_size?: number[];
      tracks?: number;
    }>;
    people?: {
      toward?: { m1?: number; m5?: number; m15?: number; total?: number };
      away?: { m1?: number; m5?: number; m15?: number; total?: number };
      [key: string]: any;
    };
    vehicles?: {
      toward?: Record<string, any>;
      away?: Record<string, any>;
      [key: string]: any;
    };
    lines?: Record<string, any>;
    [key: string]: any;
  };
  /** Several checkpoints shown as one place (e.g. a station gate counted by 6 cameras): camera tabs list
   *  every camera of the group and switching tab switches the checkpoint shown. */
  checkpoints?: CameraStreamModalProps['checkpoint'][];
  initialCameraId?: string;
  onClose: () => void;
}

// Canonical vehicle breakdown rows
const VEHICLE_ROWS = [
  { key: 'auto', label: 'Auto (3-wheeler) (in/out)' },
  { key: 'bus', label: 'Bus (in/out)' },
  { key: 'car', label: 'Car (in/out)' },
  { key: 'lorry', label: 'Lorry (in/out)' },
  { key: 'two_wheeler', label: 'Two-wheeler (in/out)' },
];

export const CameraStreamModal: React.FC<CameraStreamModalProps> = ({ 
  checkpoint: initialCheckpoint, 
  checkpoints,
  initialCameraId, 
  onClose 
}) => {
  // All checkpoints shown in this popup and every camera across them (tabs)
  const group = useMemo(
    () => (checkpoints && checkpoints.length > 0 ? checkpoints : [initialCheckpoint]),
    [checkpoints, initialCheckpoint]
  );
  const allCameras = useMemo(() => {
    const seen = new Set<string>();
    return group.flatMap(cp => cp.cameras || []).filter(c => (seen.has(c.id) ? false : (seen.add(c.id), true)));
  }, [group]);
  // Time-Series State
  const TODAY = 1440;   // 'since 00:00': the server clamps every window at the reporting day start
  const [seriesMinutes, setSeriesMinutes] = useState<number>(TODAY);
  const windowLabel = seriesMinutes === TODAY ? 'since 00:00' : `${seriesMinutes}m`;
  const [seriesData, setSeriesData] = useState<CheckpointSeriesData | null>(null);
  const [seriesLoading, setSeriesLoading] = useState<boolean>(true);
  const [apiStale, setApiStale] = useState<boolean>(false);   // last background poll failed (analytics server restarting?)

  // Camera Status State: resolve camera prioritizing initialCameraId if provided
  const resolvedCamId = useMemo(() => {
    if (initialCameraId) {
      // Check if matches a camera on the checkpoint
      const match = allCameras.find(c => c.id === initialCameraId);
      if (match) return match.id;
      return initialCameraId;
    }
    return initialCheckpoint.cameras?.[0]?.id || allCameras[0]?.id || '';
  }, [allCameras, initialCheckpoint, initialCameraId]);

  const [selectedCamId, setSelectedCamId] = useState<string>(resolvedCamId);

  // count-line editor: lines come from /config for the selected camera
  const [editLines, setEditLines] = useState<boolean>(false);
  const [camLines, setCamLines] = useState<CountLine[]>([]);
  // counting area (outline on the picture): with it, "crowd in view" only counts people inside it
  const [editZone, setEditZone] = useState<boolean>(false);
  const [camZone, setCamZone] = useState<[number, number][]>([]);
  useEffect(() => {
    setEditLines(false);
    setEditZone(false);
    setCamZone([]);
    if (!selectedCamId) return;
    baseapi.get('/config').then(res => {
      const cam = (res.data?.cameras || []).find((c: any) => c.id === selectedCamId);
      setCamLines(((cam?.lines || []) as any[]).map(l => ({ ...l, role: l.role || 'both', anchor: l.anchor || 'center' })));
      setCamZone(Array.isArray(cam?.count_zone) ? cam.count_zone : []);
    }).catch(() => setCamLines([]));
  }, [selectedCamId]);

  // Keep selectedCamId in sync if initialCameraId or checkpoint changes
  useEffect(() => {
    setSelectedCamId(resolvedCamId);
  }, [resolvedCamId]);

  // The checkpoint the selected camera counts (title, series chart and line table follow the tab)
  const checkpoint = useMemo(
    () => group.find(cp => cp.cameras?.some(c => c.id === selectedCamId)) || initialCheckpoint,
    [group, selectedCamId, initialCheckpoint]
  );
  // Callers hand over a /summary snapshot taken when the popup opened; poll our own copy so the
  // per-line 1m/5m/15m/total figures keep moving while the popup stays open. Every 1 s (was 3 s,
  // 10 Oct 2026): a crossing showed on the video overlay at once but reached this table up to 3 s
  // later. /summary answers in ~20 ms and this only runs while the popup is open.
  const [liveCp, setLiveCp] = useState<CameraStreamModalProps['checkpoint'] | null>(null);
  useEffect(() => {
    setLiveCp(null);
    let alive = true;
    // Every 15 s (12 Oct 2026; was 1 s): this answer is ~125 KB (all cameras). The live per-line figures of the
    // selected camera now come with the 1 s status answer (~3 KB); this only refreshes names / other lines.
    let busy = false;
    const load = () => {
      if (busy) return;
      busy = true;
      baseapi.get('/summary')
        .then(res => { if (alive) setLiveCp(res.data?.checkpoints?.[checkpoint.id] || null); })
        .catch(() => { /* keep the last figures; the stale banner covers outages */ })
        .finally(() => { busy = false; });
    };
    load();
    const t = window.setInterval(load, 15000);
    return () => { alive = false; window.clearInterval(t); };
  }, [checkpoint.id]);
  const cpData = useMemo(
    () => (liveCp ? { ...checkpoint, ...liveCp, cameras: checkpoint.cameras } : checkpoint),
    [checkpoint, liveCp]
  );
  // Vehicle classes are counted only where vehicles pass: railway-station and bus-stand exits (a `hub`) and
  // the city junctions (junction checkpoints outside the temple groups). Temple, queue-line and holding-area
  // cameras count people only, so their five vehicle rows were always 0/0 - not shown there (11 Oct 2026).
  const showVehicles = !!cpData.hub || (cpData.kind === 'junction' && !cpData.group);
  const [cameraStatus, setCameraStatus] = useState<CameraStatusData | null>(null);
  // people in view now (tracked in the current frame), kept for 10 min to show the peak
  const [crowdHist, setCrowdHist] = useState<{ t: number; n: number }[]>([]);
  useEffect(() => { setCrowdHist([]); }, [selectedCamId]);
  useEffect(() => {
    if (typeof cameraStatus?.tracks !== 'number') return;
    const now = Date.now();
    setCrowdHist(h => [...h.filter(p => now - p.t < 600_000), { t: now, n: cameraStatus.tracks }]);
  }, [cameraStatus]);
  const crowdNow = typeof cameraStatus?.tracks === 'number' ? cameraStatus.tracks : null;
  const crowdPeak = crowdHist.reduce((m, p) => Math.max(m, p.n), 0);
  const crowdAvg = crowdHist.length ? Math.round(crowdHist.reduce((a, p) => a + p.n, 0) / crowdHist.length) : 0;
  const [cameraLoading, setCameraLoading] = useState<boolean>(true);
  const [isLiveCamera, setIsLiveCamera] = useState<boolean>(false);
  const [classFilter, setClassFilter] = useState<string>('ALL');
  const [streamError, setStreamError] = useState<boolean>(false);
  // per selected camera: 'waiting' until the first MJPEG frame, 'none' if nothing arrives in 10 s
  const [frameState, setFrameState] = useState<'waiting' | 'ok' | 'none'>('waiting');
  const streamImgRef = React.useRef<HTMLImageElement | null>(null);
  useEffect(() => {
    setFrameState('waiting');
    setStreamError(false);
    if (!selectedCamId) return;
    const started = Date.now();
    const t = window.setInterval(() => {
      if ((streamImgRef.current?.naturalWidth || 0) > 1) { setFrameState('ok'); window.clearInterval(t); }
      else if (Date.now() - started > 10000) { setFrameState('none'); }
    }, 400);
    const el = streamImgRef.current;
    return () => {
      window.clearInterval(t);
      // Chrome keeps an MJPEG connection alive after the <img> is removed; blank it so the
      // browser's 6-connections-per-host budget is freed when the camera or modal changes.
      if (el) { el.onerror = null; el.src = ''; }
    };
  }, [selectedCamId]);
  const [lastRefreshed, setLastRefreshed] = useState<string>('');

  // Directional & Vehicle Line Breakdown Table Data (filtered to selectedCamId)
  const lineEntries = useMemo(() => {
    if (cpData.lines && Object.keys(cpData.lines).length > 0) {
      const allLines = Object.entries(cpData.lines).map(([key, line]: [string, any]) => ({
        key,
        camera_id: line.camera_id || (key.includes(':') ? key.split(':')[0] : undefined),
        line_id: line.line_id || key,
        label: line.label || key,
        toward_label: line.toward_label || 'Toward',
        away_label: line.away_label || 'Away',
        people: line.people || {},
        vehicles: line.vehicles || {}
      }));

      // Filter lines belonging to the currently selected camera
      const matchingCamLines = allLines.filter((l) => {
        if (!selectedCamId) return true;
        if (l.camera_id && l.camera_id === selectedCamId) return true;
        if (l.key.startsWith(`${selectedCamId}:`)) return true;
        return false;
      });

      // this camera's rows (1M / 5M / 15M / TOTAL) from the 1 s status answer (12 Oct 2026)
      const liveLines = cameraStatus?.live?.lines;
      const withLive = (l: typeof allLines[number]) => {
        const v = liveLines?.[l.line_id];
        // deeksha devotees (cameras with "count_deeksha"): saved in the database, so they survive restarts
        const dk = (cameraStatus?.live as any)?.deeksha?.[l.line_id];
        return v ? { ...l, deeksha: dk, people: { ...l.people, toward: v.toward, away: v.away } } : l;
      };
      const shown = matchingCamLines.length > 0 ? matchingCamLines.map(withLive) : allLines;

      // Dedicated lines: one line counts only "toward" and another only "away" (ids L-TOWARD / L-AWAY).
      // Listed separately, each block shows 0 for its other direction and the busy direction sits out of
      // sight below, so the table looks frozen. Show them as ONE block with both directions side by side.
      const tot = (m: any) => Object.values(m || {}).reduce((n: number, v: any) => n + (v?.total ?? 0), 0) as number;
      const oneWay = (l: typeof shown[number]) => {
        const t = (l.people?.toward?.total ?? 0) + tot(l.vehicles?.toward);
        const w = (l.people?.away?.total ?? 0) + tot(l.vehicles?.away);
        return /^L-(TOWARD|AWAY)/i.test(l.line_id) || t === 0 || w === 0;
      };
      const isQueue = shown.some(l => QUEUE_LINE_RE.test(l.line_id) || l.line_id === 'TOTAL');
      if (!isQueue && shown.length >= 2 && shown.every(oneWay)) {
        const add = (x: any = {}, y: any = {}) => ({
          m1: (x.m1 ?? 0) + (y.m1 ?? 0), m5: (x.m5 ?? 0) + (y.m5 ?? 0),
          m15: (x.m15 ?? 0) + (y.m15 ?? 0), total: (x.total ?? 0) + (y.total ?? 0),
        });
        const addVeh = (x: any = {}, y: any = {}) => {
          const out: Record<string, any> = { ...x };
          Object.entries(y).forEach(([cls, v]) => { out[cls] = add(out[cls], v as any); });
          return out;
        };
        const towardLine = shown.find(l => /TOWARD/i.test(l.line_id)) || shown.find(l => (l.people?.toward?.total ?? 0) > 0) || shown[0];
        const awayLine = shown.find(l => /AWAY/i.test(l.line_id)) || shown.find(l => (l.people?.away?.total ?? 0) > 0) || shown[shown.length - 1];
        return [shown.reduce((acc, l) => ({
          ...acc,
          people: { toward: add(acc.people?.toward, l.people?.toward), away: add(acc.people?.away, l.people?.away) },
          vehicles: { toward: addVeh(acc.vehicles?.toward, l.vehicles?.toward), away: addVeh(acc.vehicles?.away, l.vehicles?.away) },
        }), {
          key: `${selectedCamId || cpData.id}:combined`,
          camera_id: shown[0].camera_id,
          line_id: 'COMBINED',
          label: `${cpData.name || cpData.id} · ${shown.length} lines combined`,
          toward_label: towardLine.toward_label,
          away_label: awayLine.away_label,
          people: {} as any,
          vehicles: {} as any,
        })];
      }

      return shown;
    }
    return [{
      key: cpData.id,
      camera_id: selectedCamId,
      line_id: 'L1',
      label: cpData.name || cpData.id,
      toward_label: 'Toward',
      away_label: 'Away',
      people: cpData.people || {},
      vehicles: cpData.vehicles || {}
    }];
  }, [cpData, selectedCamId, cameraStatus?.live]);

  // Queue-lane camera: one count line per lane (Q1..Qn) -> per-lane rows and one chart series per lane
  const queueLines = useMemo(
    () => lineEntries
      .filter(l => QUEUE_LINE_RE.test(l.line_id))
      .sort((a, b) => Number(a.line_id.slice(1)) - Number(b.line_id.slice(1))),
    [lineEntries]
  );
  const queueMode = queueLines.length >= 2;
  const totalLine = useMemo(() => lineEntries.find(l => l.line_id === 'TOTAL'), [lineEntries]);
  const queueColor = (id: string) => QUEUE_COLORS[(Number(id.slice(1)) - 1 + QUEUE_COLORS.length) % QUEUE_COLORS.length];

  // 1. Fetch Time-Series Data: /checkpoints/{id}/series?minutes={minutes}
  const [occData, setOccData] = useState<{ t: number; avg: number; max: number }[]>([]);
  const seriesBusy = React.useRef(false);
  const fetchSeries = useCallback(async (minutes = seriesMinutes, silent = false) => {
    if (silent && seriesBusy.current) return;      // the previous chart request is still on its way
    seriesBusy.current = true;
    if (!silent) setSeriesLoading(true);
    if (selectedCamId) {
      // crowd-in-view trend (per-minute average / peak); optional: older servers do not have it
      baseapi.get(`/cameras/${encodeURIComponent(selectedCamId)}/occupancy?minutes=${minutes}`)
        .then(r => setOccData(Array.isArray(r.data?.points) ? r.data.points : []))
        .catch(() => { if (!silent) setOccData([]); });
    }
    try {
      // per selected camera, so Toward / Away / Veh / Net reflect the stream being watched
      const camQ = selectedCamId ? `&camera_id=${encodeURIComponent(selectedCamId)}` : '';
      const res = await baseapi.get(`/checkpoints/${encodeURIComponent(checkpoint.id)}/series?minutes=${minutes}${camQ}`);
      if (res && res.data && Array.isArray(res.data.points)) {
        setSeriesData(res.data);
        setApiStale(false);
      } else if (!silent) {
        setSeriesData(null);
      }
    } catch (err) {
      console.warn(`Live /checkpoints/${checkpoint.id}/series request failed:`, err);
      if (!silent) setSeriesData(null);          // background poll failed: keep the last good chart
      setApiStale(true);
    } finally {
      seriesBusy.current = false;
      setSeriesLoading(false);
    }
  }, [checkpoint.id, seriesMinutes, selectedCamId]);

  // 2. Fetch Camera Status: /cameras/{camera_id}/status
  const statusBusy = React.useRef(false);
  const fetchCameraStatus = useCallback(async (camId: string, silent = false) => {
    // never stack requests: a late 1 s poll must not start another one behind it (12 Oct 2026)
    if (silent && statusBusy.current) return;
    if (!camId) {
      setCameraStatus(null);
      setCameraLoading(false);
      setStreamError(false);
      setIsLiveCamera(false);
      return;
    }
    if (!silent) { setCameraLoading(true); setStreamError(false); }
    statusBusy.current = true;
    try {
      const res = await baseapi.get(`/cameras/${encodeURIComponent(camId)}/status`);
      if (res && res.data && (res.data.state || res.data.recent || res.data.recent_crossings || res.data.source || res.data.detail)) {
        // the analytics server names the list "recent_crossings"; keep "recent" for the UI
        if (!res.data.recent && res.data.recent_crossings) res.data.recent = res.data.recent_crossings;
        setCameraStatus(res.data);
        setIsLiveCamera(true);
        // "Synced" = time of the last GOOD answer (it used to move on failed requests too and hid a frozen panel)
        setLastRefreshed(new Date().toLocaleTimeString('en-US', { hour12: false }));
      } else {
        setCameraStatus(null);
        setIsLiveCamera(false);
      }
    } catch (err) {
      console.warn(`Live /cameras/${camId}/status request failed:`, err);
      if (!silent) { setCameraStatus(null); setIsLiveCamera(false); }   // keep last status during a blip
      setApiStale(true);
    } finally {
      statusBusy.current = false;
      setCameraLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSeries(seriesMinutes);
  }, [fetchSeries, seriesMinutes]);

  useEffect(() => {
    fetchCameraStatus(selectedCamId);
  }, [selectedCamId, fetchCameraStatus]);

  // Real-time: recent crossings + camera state + Toward/Away/Net every 1 s (was 2 s, 10 Oct 2026, so the
  // panels keep up with the count drawn on the video), in the background so they update in place
  // without a loading flicker. Each request answers in a few ms and only runs while the popup is open.
  useEffect(() => {
    if (!selectedCamId) return;
    // Status (recent crossings + this camera's live counts, ~3 KB) every 1 s; the chart / occupancy (~150 KB for
    // a day) every 10 s. Pulling the whole day every second timed out on a slow link and the figures froze, then
    // jumped (12 Oct 2026); Toward / Away / Net and the table now come with the status answer.
    let tick = 0;
    const fast = window.setInterval(() => {
      fetchCameraStatus(selectedCamId, true);
      if (++tick % 10 === 0) fetchSeries(seriesMinutes, true);
    }, 1000);
    return () => { window.clearInterval(fast); };
  }, [selectedCamId, seriesMinutes, fetchCameraStatus, fetchSeries]);

  // Transform series data for Recharts & summary statistics
  const { chartData, totalToward: seriesToward, totalAway: seriesAway, totalVehicles: seriesVehicles } = useMemo(() => {
    let rawPoints: SeriesPoint[] = seriesData?.points || [];
    if (occData.length) {
      const byT = new Map<number, SeriesPoint>(rawPoints.map(p => [p.t, { ...p }]));
      occData.forEach(o => {
        const cur = byT.get(o.t) || { t: o.t, toward: 0, away: 0, veh_toward: 0, veh_away: 0 };
        cur.crowd = o.avg; cur.crowd_max = o.max;
        byT.set(o.t, cur);
      });
      rawPoints = [...byT.values()].sort((a, b) => a.t - b.t);
    }
    // a whole day at one point per minute is too dense to read: regroup into 5-min (<= 6 h) or 15-min buckets
    if (rawPoints.length > 150) {
      const step = rawPoints.length > 400 ? 900 : 300;
      const acc = new Map<number, SeriesPoint>();
      const crowdN = new Map<number, number>();
      rawPoints.forEach((pt) => {
        const t = Math.floor(pt.t / step) * step;
        const cur = acc.get(t) || { t, toward: 0, away: 0, veh_toward: 0, veh_away: 0, lines: {} };
        cur.toward += pt.toward; cur.away += pt.away;
        cur.veh_toward += pt.veh_toward || 0; cur.veh_away += pt.veh_away || 0;
        Object.entries(pt.lines || {}).forEach(([id, v]) => {
          const l = (cur.lines![id] ||= { toward: 0, away: 0 });
          l.toward += v.toward; l.away += v.away;
        });
        if (typeof pt.crowd === 'number') {     // crowd: mean of the minute averages, peak of the peaks
          const n = (crowdN.get(t) || 0) + 1;
          cur.crowd = ((cur.crowd || 0) * (n - 1) + pt.crowd) / n;
          cur.crowd_max = Math.max(cur.crowd_max || 0, pt.crowd_max || 0);
          crowdN.set(t, n);
        }
        acc.set(t, cur);
      });
      rawPoints = [...acc.values()].sort((a, b) => a.t - b.t);
    }
    let runningAccumulation = 0;
    let maxToward = 0;
    let sumToward = 0;
    let sumAway = 0;
    let sumVehicles = 0;

    const formatted = rawPoints.map((pt) => {
      sumToward += pt.toward;
      sumAway += pt.away;
      sumVehicles += (pt.veh_toward || 0) + (pt.veh_away || 0);
      if (pt.toward > maxToward) maxToward = pt.toward;
      
      const netDelta = pt.toward - pt.away;
      runningAccumulation += netDelta;

      const dateObj = new Date(pt.t * 1000);
      const timeStr = dateObj.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit', 
        hour12: false 
      });

      const perLine: Record<string, number> = {};
      Object.entries(pt.lines || {}).forEach(([id, v]) => { perLine[`q_${id}`] = v.toward; });

      return {
        ...pt,
        ...perLine,
        time: timeStr,
        fullTime: dateObj.toLocaleTimeString('en-US', { hour12: false }),
        netDelta,
        cumulative: runningAccumulation,
        totalFlow: pt.toward + pt.away
      };
    });

    const net = sumToward - sumAway;

    return {
      chartData: formatted,
      totalToward: sumToward,
      totalAway: sumAway,
      netBalance: net,
      peakInflow: maxToward,
      totalVehicles: sumVehicles
    };
  }, [seriesData, occData]);
  // this camera's live counts from the 1 s status answer; the chart sums only as a fallback (older backend)
  const liveCounts = cameraStatus?.live;
  const totalToward = liveCounts ? liveCounts.toward : seriesToward;
  const totalAway = liveCounts ? liveCounts.away : seriesAway;
  const totalVehicles = liveCounts ? liveCounts.veh_toward + liveCounts.veh_away : seriesVehicles;
  const netBalance = totalToward - totalAway;

  // Camera detections filtering logic
  const recentList = cameraStatus?.recent || [];
  const filteredRecent = recentList.filter((item) => {
    if (classFilter !== 'ALL' && item.cls !== classFilter) return false;
    return true;
  });



  
  const formatTimeAgo = (unixTs: number) => {
    const diff = Math.floor(Date.now() / 1000 - unixTs);
    if (diff <= 3) return 'Just now';
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return new Date(unixTs * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const getClassBadge = (cls: string) => {
    switch (cls) {
      case 'person':
        return { label: 'Person', bg: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/25', icon: <User className="w-2.5 h-2.5" /> };
      case 'auto':
        return { label: 'Auto', bg: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/25', icon: <Car className="w-2.5 h-2.5" /> };
      case 'two_wheeler':
        return { label: '2-Wheel', bg: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/25', icon: <Zap className="w-2.5 h-2.5" /> };
      case 'car':
        return { label: 'Car', bg: 'bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/25', icon: <Car className="w-2.5 h-2.5" /> };
      default:
        return { label: cls.replace('_', ' '), bg: 'bg-neutral-500/10 text-neutral-600 dark:text-neutral-400 border-neutral-500/25', icon: <Car className="w-2.5 h-2.5" /> };
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-xs flex items-center justify-center p-2.5 sm:p-4">
      <div className="w-full max-w-[96vw] 2xl:max-w-[1560px] h-[92vh] max-h-[92vh] bg-white dark:bg-[#14161f] border border-neutral-200 dark:border-[#272d3b] rounded-2xl shadow-2xl flex flex-col overflow-hidden text-neutral-900 dark:text-neutral-100">
        
        {/* ================= COMPACT HEADER ================= */}
        <div className="px-3.5 py-2 bg-neutral-50 dark:bg-[#181b24] border-b border-neutral-200 dark:border-[#242937] flex items-center justify-between gap-2.5 shrink-0">
          <div className="flex items-center gap-2.5 truncate">
            <span className="text-[11px] font-extrabold px-2 py-0.5 rounded bg-blue-500 text-white shrink-0">
              {checkpoint.id}
            </span>
            <h2 className="text-xs sm:text-[13px] font-bold text-neutral-900 dark:text-neutral-100 truncate">
              {checkpoint.name}
            </h2>
            {checkpoint.cameras && checkpoint.cameras.length > 0 && selectedCamId ? (
              <span className={`hidden sm:flex text-[9.5px] px-1.5 py-0.2 rounded font-semibold border items-center gap-1 ${
                isLiveCamera 
                  ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/25' 
                  : 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/25'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${isLiveCamera ? 'bg-emerald-500 animate-ping' : 'bg-blue-500'}`} />
                {isLiveCamera ? 'Live' : 'Snapshot'}
              </span>
            ) : (
              <span className="hidden sm:flex text-[9.5px] px-1.5 py-0.2 rounded font-semibold border items-center gap-1 bg-neutral-500/10 text-neutral-500 dark:text-neutral-400 border-neutral-500/25">
                <VideoOff className="w-2.5 h-2.5" />
                No Camera
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {/* Multi-Camera Selector pills */}
            {allCameras.length > 1 && (
              <div className="flex flex-wrap items-center gap-1 max-w-[52vw] bg-neutral-200/80 dark:bg-[#1f2431] p-0.5 rounded-lg border border-neutral-300 dark:border-[#2a3040]">
                {allCameras.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setSelectedCamId(c.id)}
                    title={group.find(cp => cp.cameras?.some(x => x.id === c.id))?.name || c.id}
                    className={`px-2 py-0.5 rounded text-[10.5px] font-bold transition-colors cursor-pointer ${
                      selectedCamId === c.id
                        ? 'bg-blue-500 text-white shadow-2xs'
                        : 'text-neutral-600 dark:text-neutral-400'
                    }`}
                  >
                    {c.id}
                  </button>
                ))}
              </div>
            )}

            <button
              onClick={() => setEditLines(v => !v)}
              disabled={!selectedCamId}
              className={`p-1 rounded-lg transition-colors cursor-pointer ${editLines ? 'bg-emerald-500 text-black' : 'bg-neutral-200/70 dark:bg-[#252a37] text-neutral-700 dark:text-neutral-300 hover:text-emerald-500'}`}
              title="Edit the two count lines (toward / away) on this camera"
            >
              <PencilRuler className="w-3.5 h-3.5" />
            </button>

            <button
              onClick={() => { setEditLines(false); setEditZone(v => !v); }}
              disabled={!selectedCamId}
              className={`p-1 rounded-lg transition-colors cursor-pointer ${editZone ? 'bg-cyan-500 text-black' : camZone.length ? 'bg-cyan-500/20 text-cyan-500' : 'bg-neutral-200/70 dark:bg-[#252a37] text-neutral-700 dark:text-neutral-300 hover:text-cyan-500'}`}
              title="Draw the counting area: only people inside it are counted as crowd in view"
            >
              <Scan className="w-3.5 h-3.5" />
            </button>

            <button
              onClick={() => {
                fetchSeries(seriesMinutes);
                if (selectedCamId) fetchCameraStatus(selectedCamId);
              }}
              disabled={seriesLoading || cameraLoading}
              className="p-1 rounded-lg bg-neutral-200/70 dark:bg-[#252a37] text-neutral-700 dark:text-neutral-300 hover:text-blue-500 hover:bg-neutral-200 dark:hover:bg-[#2c3242] transition-colors cursor-pointer"
              title={apiStale ? 'Analytics server not answering - showing last received data' : "Refresh Feeds"}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${(seriesLoading || cameraLoading) ? 'animate-spin' : ''}`} />
            </button>

            <button
              onClick={onClose}
              className="p-1 rounded-lg bg-neutral-200/70 dark:bg-[#252a37] text-neutral-500 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-200 dark:hover:bg-[#2c3242] transition-colors cursor-pointer"
              title="Close Dialog"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* ================= ALL-IN-ONE FIXED NON-SCROLLING BODY ================= */}
        <div className="flex-1 overflow-hidden p-3 sm:p-3.5 flex flex-col min-h-0">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5 items-stretch flex-1 min-h-0">
            
            {/* ================= LEFT SIDE: STREAM + FLOW GRAPH (lg:col-span-7) ================= */}
            <div className="lg:col-span-7 flex flex-col space-y-2 h-full min-h-0 overflow-hidden">
              
              {/* Surveillance Camera Stream - aspect-video & object-contain ensures video is NEVER cut */}
              <div className="relative aspect-video max-h-[44vh] w-full rounded-xl bg-[#090b10] border border-neutral-800 overflow-hidden shadow-md flex items-center justify-center group shrink-0">
                
                {/* Direct MJPEG Stream, No Camera message, or Fallback UI */}
                {!selectedCamId ? (
                  <div className="relative w-full h-full bg-[#0c0e15] flex flex-col items-center justify-center select-none text-neutral-400 gap-2.5 p-4 text-center">
                    <div className="w-12 h-12 rounded-xl bg-neutral-800/60 border border-neutral-700/60 flex items-center justify-center text-neutral-400">
                      <VideoOff className="w-6 h-6 text-neutral-400" />
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm font-bold text-neutral-200">No Camera Configured</div>
                      <p className="text-[11px] text-neutral-400 mt-1 max-w-xs">
                        No active CCTV camera feed is mapped to this location ({checkpoint.name}).
                      </p>
                    </div>
                  </div>
                ) : !streamError ? (
                  <>
                    {/* keyed per camera: a fresh element, so the previous camera's last frame never lingers */}
                    <LiveFrame
                      key={selectedCamId}
                      ref={streamImgRef}
                      src={`${API_BASE}cameras/${encodeURIComponent(selectedCamId)}/snapshot.jpg`}
                      alt={`Stream ${selectedCamId}`}
                      className="w-full h-full object-contain bg-black"
                      onError={() => setStreamError(true)}
                    />
                    {editLines && (
                      <LineEditor
                        cameraId={selectedCamId}
                        imgRef={streamImgRef}
                        initialLines={camLines}
                        onSaved={(ls) => { setCamLines(ls); }}
                        onCancel={() => setEditLines(false)}
                      />
                    )}
                    {editZone && (
                      <div className="absolute inset-0 z-20 bg-[#0f1218] overflow-auto">
                        <ZoneEditor
                          cameraId={selectedCamId}
                          initial={camZone}
                          onSaved={(pts) => setCamZone(pts)}
                          onClose={() => setEditZone(false)}
                        />
                      </div>
                    )}
                    {frameState !== 'ok' && !editLines && !editZone && (
                      <div className="absolute inset-0 bg-[#0c0e15] flex flex-col items-center justify-center gap-2 text-center p-4 select-none">
                        {frameState === 'waiting' ? (
                          <>
                            <div className="w-8 h-8 rounded-full border-2 border-white/15 border-t-blue-500 animate-spin" />
                            <span className="text-[11px] text-neutral-300">Connecting to {selectedCamId}…</span>
                          </>
                        ) : (
                          <>
                            <VideoOff className="w-6 h-6 text-rose-400" />
                            <span className="text-xs font-bold text-neutral-200">No video from {selectedCamId}</span>
                            <span className="text-[10.5px] text-neutral-400 max-w-xs">
                              The analytics server is not receiving frames from this camera. Counts for it are paused.
                            </span>
                            <button
                              onClick={() => { const cam = selectedCamId; setSelectedCamId(''); window.setTimeout(() => setSelectedCamId(cam), 50); }}
                              className="mt-1 text-[10px] px-2 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-500/40 hover:bg-blue-500/30 cursor-pointer"
                            >
                              Retry
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="relative w-full h-full bg-[#0c0e15] flex items-center justify-center overflow-hidden select-none">
                    <div 
                      className="absolute inset-0 opacity-20"
                      style={{
                        backgroundImage: 'radial-gradient(#f97316 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.02) 50%, transparent 50%)',
                        backgroundSize: '20px 20px, 100% 4px'
                      }}
                    />

                    {/* Corner Reticles */}
                    <div className="absolute top-2.5 left-2.5 w-3.5 h-3.5 border-t-2 border-l-2 border-orange-500/80" />
                    <div className="absolute top-2.5 right-2.5 w-3.5 h-3.5 border-t-2 border-r-2 border-orange-500/80" />
                    <div className="absolute bottom-2.5 left-2.5 w-3.5 h-3.5 border-b-2 border-l-2 border-orange-500/80" />
                    <div className="absolute bottom-2.5 right-2.5 w-3.5 h-3.5 border-b-2 border-r-2 border-orange-500/80" />

                    {/* Center Crosshair & Reconnect */}
                    <div className="flex flex-col items-center gap-1.5 z-10">
                      <div className="w-10 h-10 border border-white/20 rounded-full flex items-center justify-center">
                        <div className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-ping" />
                      </div>
                      <span className="text-[10px] text-neutral-400">RTSP Stream Connecting...</span>
                      <button 
                        onClick={() => setStreamError(false)}
                        className="text-[9.5px] px-2 py-0.5 rounded bg-orange-500/20 text-orange-400 border border-orange-500/40 hover:bg-orange-500/30 cursor-pointer transition-colors"
                      >
                        Retry Stream
                      </button>
                    </div>
                  </div>
                )}

                {/* Top OSD Information (only when camera is present) */}
                {selectedCamId && (
                  <div className="absolute top-2 inset-x-2 flex items-center justify-between text-white text-[9.5px] pointer-events-none">
                    <div className="flex items-center gap-1.5 bg-black/75 px-2 py-0.5 rounded backdrop-blur-xs border border-white/10">
                      <span className={`w-1.5 h-1.5 rounded-full ${cameraStatus ? 'bg-emerald-500 animate-pulse' : 'bg-neutral-500'}`} />
                      <span className="font-extrabold">{selectedCamId}</span>
                      <span className="text-white/40">|</span>
                      <span className={`font-bold ${cameraStatus ? 'text-orange-400' : 'text-neutral-400'}`}>
                        {cameraStatus?.state ? cameraStatus.state.toUpperCase() : (cameraStatus ? 'LIVE' : 'NO DATA')}
                      </span>
                      {cameraStatus?.frame_age_s !== undefined && (
                        <>
                          <span className="text-white/40">|</span>
                          <span>{cameraStatus?.frame_age_s?.toFixed(1)}s</span>
                        </>
                      )}
                    </div>

                    {cameraStatus ? (
                      <div className="flex items-center gap-1.5 bg-black/75 px-2 py-0.5 rounded backdrop-blur-xs border border-white/10">
                        {cameraStatus.proc_fps !== undefined && (
                          <span className="text-emerald-400 font-bold">{cameraStatus.proc_fps} FPS</span>
                        )}
                        {cameraStatus.frame_size && (
                          <>
                            <span className="text-white/40">|</span>
                            <span>{`${cameraStatus.frame_size[0]}x${cameraStatus.frame_size[1]}`}</span>
                          </>
                        )}
                        {cameraStatus.decoder && (
                          <>
                            <span className="text-white/40">|</span>
                            <span className="text-orange-400 font-bold">{cameraStatus.decoder}</span>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="bg-black/75 px-2 py-0.5 rounded backdrop-blur-xs border border-white/10 text-neutral-400">
                        No Telemetry Data
                      </div>
                    )}
                  </div>
                )}

                {/* Bottom OSD Bar (only when camera is present) */}
                {selectedCamId && (
                  <div className="absolute bottom-2 inset-x-2 flex items-center justify-end text-white text-[9px] pointer-events-none">
                    {/* camera IP / RTSP path intentionally not shown to operators */}
                    {cameraStatus && cameraStatus.tracks !== undefined ? (
                      <div className="bg-orange-500 text-white font-extrabold px-1.5 py-0.5 rounded shadow-xs flex items-center gap-1">
                        <Activity className="w-2.5 h-2.5" />
                        <span>{cameraStatus.tracks} Targets</span>
                      </div>
                    ) : (
                      <div className="bg-black/75 text-neutral-400 px-1.5 py-0.5 rounded border border-white/10">
                        No Targets
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Viewport Compact Controls */}
              <div className="flex items-center justify-between px-2.5 py-1 rounded-lg bg-neutral-100 dark:bg-[#181b24] border border-neutral-200 dark:border-[#242937] text-[10.5px] shrink-0">
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1 text-emerald-500 font-bold">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    {selectedCamId ? 'MJPEG Live Stream' : 'No Camera Feed'}
                  </span>
                  {selectedCamId && (
                    <>
                      <span className="text-neutral-400">•</span>
                      <span className="text-neutral-600 dark:text-neutral-300 font-medium">
                        {selectedCamId}
                      </span>
                    </>
                  )}
                </div>

                <span className="text-[10px] text-neutral-400">
                  Synced: {lastRefreshed || 'Active'}
                </span>
              </div>

              {/* Flow Dynamics Dual Area Chart - Directly Below the Stream */}
              <div className="p-2.5 rounded-xl bg-neutral-50 dark:bg-[#181b24] border border-neutral-200 dark:border-[#242937] space-y-1.5 flex-1 flex flex-col justify-between min-h-0 overflow-hidden">
                <div className="flex items-center justify-between pb-1 border-b border-neutral-200 dark:border-[#222736] shrink-0">
                  <div className="flex items-center gap-1.5">
                    <TrendingUp className="w-3.5 h-3.5 text-orange-500" />
                    <span className="text-[11.5px] font-extrabold uppercase tracking-wide text-neutral-800 dark:text-neutral-200">
                      Flow Dynamics ({windowLabel})
                    </span>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Ingress / Egress Legend */}
                    {occData.length > 0 && (
                      <span className="flex items-center gap-1 font-bold text-emerald-500 text-[10px]" title="Average people in the camera's view per minute (right axis)">
                        <span className="w-2 h-0.5 bg-emerald-500" />
                        Crowd in view
                      </span>
                    )}
                    {queueMode ? (
                      <div className="flex items-center gap-2 text-[10px]">
                        {queueLines.map(q => (
                          <span key={q.key} className="flex items-center gap-1 font-bold" style={{ color: queueColor(q.line_id) }}>
                            <span className="w-2 h-2 rounded-full" style={{ background: queueColor(q.line_id) }} />
                            {q.line_id}
                          </span>
                        ))}
                        <span className="flex items-center gap-1 font-bold text-neutral-400">
                          <span className="w-2 h-0.5 bg-neutral-400" />
                          {totalLine ? 'Total line' : 'All'}
                        </span>
                      </div>
                    ) : (
                    <div className="flex items-center gap-2 text-[10px]">
                      <span className="flex items-center gap-1 font-bold text-orange-500">
                        <span className="w-2 h-2 rounded-full bg-orange-500" />
                        Toward (In)
                      </span>
                      <span className="flex items-center gap-1 font-bold text-sky-500">
                        <span className="w-2 h-2 rounded-full bg-sky-500" />
                        Away (Out)
                      </span>
                    </div>
                    )}

                    {/* Window Range */}
                    <div className="flex items-center p-0.5 bg-neutral-200 dark:bg-[#202532] rounded border border-neutral-300 dark:border-[#293040]">
                      {[TODAY, 15, 30, 60, 120].map((m) => (
                        <button
                          key={m}
                          onClick={() => setSeriesMinutes(m)}
                          className={`px-1.5 py-0.5 rounded text-[10px] font-bold transition-colors cursor-pointer ${
                            seriesMinutes === m ? 'bg-white dark:bg-[#2d3445] text-orange-500' : 'text-neutral-500'
                          }`}
                          title={m === TODAY ? 'Midnight to now (default)' : `Last ${m} minutes`}
                        >
                          {m === TODAY ? 'Today' : `${m}m`}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Dual Flow Area Chart */}
                {seriesLoading ? (
                  <div className="flex-1 flex flex-col items-center justify-center text-neutral-400 gap-2 min-h-[140px]">
                    <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                    <span className="text-[11px]">Loading series telemetry...</span>
                  </div>
                ) : chartData.length === 0 ? (
                  <div className="flex-1 flex flex-col items-center justify-center text-neutral-400 gap-1.5 py-6 min-h-[140px]">
                    <Database className="w-7 h-7 text-neutral-300 dark:text-neutral-600" />
                    <span className="text-xs font-semibold text-neutral-600 dark:text-neutral-300">No time-series data available</span>
                    <span className="text-[10px] text-neutral-400 dark:text-neutral-500">No telemetry recorded for this checkpoint {seriesMinutes === TODAY ? 'since 00:00' : `in the past ${seriesMinutes}m`}</span>
                  </div>
                ) : (
                  <div className="flex-1 min-h-0 w-full pt-0.5">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData} margin={{ top: 5, right: occData.length > 0 ? 0 : 5, left: -25, bottom: 0 }}>
                        <XAxis 
                          dataKey="time" 
                          stroke="#8892b0" 
                          fontSize={9.5} 
                          tickLine={false}
                          interval={Math.max(1, Math.floor(chartData.length / 7))}
                        />
                        <YAxis 
                          stroke="#8892b0" 
                          fontSize={9.5} 
                          tickLine={false}
                          allowDecimals={false}
                        />
                        {occData.length > 0 && (
                          <YAxis yAxisId="crowd" orientation="right" stroke="#10b981" fontSize={9.5} tickLine={false} allowDecimals={false} width={28} />
                        )}
                        <Tooltip
                          content={({ active, payload }) => {
                            if (!active || !payload || !payload.length) return null;
                            const data = payload[0].payload as SeriesPoint & { fullTime: string; netDelta: number };
                            return (
                              <div className="p-2 rounded-lg bg-neutral-900/95 dark:bg-[#12141c]/95 border border-neutral-700 dark:border-[#2d3445] shadow-lg text-[10.5px] space-y-1">
                                <div className="font-bold text-neutral-400 border-b border-neutral-800 pb-0.5">
                                  {data.fullTime}
                                </div>
                                {queueMode && queueLines.map(q => (
                                  <div key={q.key} className="font-bold flex justify-between gap-3" style={{ color: queueColor(q.line_id) }}>
                                    <span>{q.line_id}:</span> <span>+{data.lines?.[q.line_id]?.toward ?? 0}</span>
                                  </div>
                                ))}
                                <div className="text-orange-400 font-bold flex justify-between gap-3">
                                  <span>↑ Toward:</span> <span>+{data.toward}</span>
                                </div>
                                {typeof data.crowd === 'number' && (
                                  <div className="text-emerald-400 font-bold flex justify-between gap-3">
                                    <span>Crowd in view:</span> <span>{Math.round(data.crowd)} (peak {data.crowd_max ?? 0})</span>
                                  </div>
                                )}
                                <div className="text-sky-400 font-bold flex justify-between gap-3">
                                  <span>↓ Away:</span> <span>+{data.away}</span>
                                </div>
                                <div className="font-extrabold text-neutral-200 border-t border-neutral-800 pt-0.5 flex justify-between gap-3">
                                  <span>Net:</span>
                                  <span className={data.netDelta >= 0 ? 'text-emerald-400' : 'text-amber-400'}>
                                    {data.netDelta >= 0 ? `+${data.netDelta}` : data.netDelta}
                                  </span>
                                </div>
                              </div>
                            );
                          }}
                        />
                        {queueMode ? (
                          <>
                            {queueLines.map(q => (
                              <Area
                                key={q.key}
                                type="monotone"
                                dataKey={`q_${q.line_id}`}
                                name={q.line_id}
                                stroke={queueColor(q.line_id)}
                                strokeWidth={2}
                                fill={queueColor(q.line_id)}
                                fillOpacity={0.08}
                                connectNulls
                              />
                            ))}
                            <Line type="monotone" dataKey="toward" name={totalLine ? 'Single total line' : 'All lanes'} stroke="#9ca3af" strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
                          </>
                        ) : (
                          <>
                        <Area 
                          type="monotone" 
                          dataKey="toward" 
                          name="Toward (In)" 
                          stroke="#f97316" 
                          strokeWidth={2}
                          fill="#f97316" 
                          fillOpacity={0.15} 
                        />
                        <Area 
                          type="monotone" 
                          dataKey="away" 
                          name="Away (Out)" 
                          stroke="#0ea5e9" 
                          strokeWidth={2}
                          fill="#0ea5e9" 
                          fillOpacity={0.15} 
                        />
                          </>
                        )}
                        {occData.length > 0 && (
                          <Line yAxisId="crowd" type="monotone" dataKey="crowd" name="Crowd in view" stroke="#10b981" strokeWidth={2} dot={false} connectNulls />
                        )}
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>

            {/* ================= RIGHT SIDE: ALL DATA & CROSSINGS (lg:col-span-5) ================= */}
            <div className="lg:col-span-5 flex flex-col space-y-2 h-full min-h-0 overflow-hidden">
              
              {/* 1. 60-Min Ingress / Egress Stats Chips */}
              {crowdNow !== null && (
                <div className="p-1.5 px-2.5 rounded-lg bg-emerald-500/5 border border-emerald-500/30 flex items-center justify-between text-xs shrink-0"
                     title="People detected in this camera's view right now (crowd count), with the peak and average of the last 10 minutes">
                  <span className="text-[10px] font-extrabold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                    <User className="w-3 h-3" /> Crowd in view now
                  </span>
                  <span className="font-extrabold text-emerald-500 text-base">{crowdNow}</span>
                  <span className="text-[10px] text-neutral-500">peak 10m <b className="text-neutral-700 dark:text-neutral-200">{crowdPeak}</b></span>
                  <span className="text-[10px] text-neutral-500">avg 10m <b className="text-neutral-700 dark:text-neutral-200">{crowdAvg}</b></span>
                </div>
              )}

              <div className="p-1.5 px-2.5 rounded-lg bg-neutral-50 dark:bg-[#181b24] border border-neutral-200 dark:border-[#232837] flex items-center justify-between text-xs shrink-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-extrabold text-orange-600 dark:text-orange-400 flex items-center">
                    <ArrowUpRight className="w-3 h-3" /> Toward:
                  </span>
                  <span className="font-extrabold text-orange-500 text-sm">{totalToward}</span>
                </div>

                <div className="h-4 w-px bg-neutral-200 dark:bg-[#282f40]" />

                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-extrabold text-sky-600 dark:text-sky-400 flex items-center">
                    <ArrowDownRight className="w-3 h-3" /> Away:
                  </span>
                  <span className="font-extrabold text-sky-500 text-sm">{totalAway}</span>
                </div>

                <div className="h-4 w-px bg-neutral-200 dark:bg-[#282f40]" />

                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-extrabold text-amber-500 flex items-center">
                    <Car className="w-3 h-3" /> Veh:
                  </span>
                  <span className="font-extrabold text-amber-500 text-sm">{totalVehicles}</span>
                </div>

                <div className="h-4 w-px bg-neutral-200 dark:bg-[#282f40]" />

                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-extrabold text-neutral-500 flex items-center">
                    Net:
                  </span>
                  <span className={`font-extrabold text-sm ${netBalance >= 0 ? 'text-emerald-500' : 'text-amber-500'}`}>
                    {netBalance > 0 ? `+${netBalance}` : netBalance}
                  </span>
                </div>
              </div>

              {/* 2. Directional & Vehicle Breakdown Table */}
              <div className="p-2.5 rounded-xl bg-neutral-50 dark:bg-[#181b24] border border-neutral-200 dark:border-[#242937] flex-1 max-h-[305px] flex flex-col min-h-0 overflow-hidden">
                <div className="flex items-center justify-between pb-1 border-b border-neutral-200 dark:border-[#222736] shrink-0">
                  <div className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wide text-neutral-800 dark:text-neutral-200">
                    <TableIcon className="w-3.5 h-3.5 text-orange-500" />
                    <span>{showVehicles ? <>Directional Flow &amp; Vehicle Breakdown</> : 'Directional Flow'}</span>
                  </div>
                  <span className="text-[9.5px] text-neutral-400 font-semibold">
                    {queueMode ? `${queueLines.length} Queue Lines` : `${lineEntries.length} ${lineEntries.length === 1 ? 'Gate Line' : 'Gate Lines'}`}
                  </span>
                </div>

                <div className="flex-1 overflow-y-auto pr-0.5 scrollbar-thin min-h-0 pt-0.5">
                  {queueMode ? (
                    <table className="w-full text-left text-[11px] border-collapse">
                      <thead className="sticky top-0 bg-neutral-100 dark:bg-[#1f2431] text-[9.5px] font-bold text-neutral-500 uppercase border-b border-neutral-200 dark:border-[#262c3b] z-10">
                        <tr>
                          <th className="py-1 px-2">Queue line (people in)</th>
                          <th className="py-1 px-1.5 text-right">1m</th>
                          <th className="py-1 px-1.5 text-right">5m</th>
                          <th className="py-1 px-1.5 text-right">15m</th>
                          <th className="py-1 px-2 text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-200/70 dark:divide-[#232838]">
                        {queueLines.map(q => {
                          const t = q.people?.toward || {};
                          const a = q.people?.away || {};
                          return (
                            <tr key={q.key} className="hover:bg-neutral-100/50 dark:hover:bg-[#1d222e] transition-colors" title={q.label}>
                              <td className="py-1 px-2 font-bold" style={{ color: queueColor(q.line_id) }}>
                                {q.line_id}
                                {(a.total ?? 0) > 0 && (
                                  <span className="ml-1.5 text-[9px] font-semibold text-sky-500">(out {a.total})</span>
                                )}
                              </td>
                              <td className="py-1 px-1.5 text-right font-semibold">{t.m1 ?? 0}</td>
                              <td className="py-1 px-1.5 text-right font-semibold">{t.m5 ?? 0}</td>
                              <td className="py-1 px-1.5 text-right font-semibold">{t.m15 ?? 0}</td>
                              <td className="py-1 px-2 text-right font-bold">{t.total ?? 0}</td>
                            </tr>
                          );
                        })}
                        {(() => {
                          const sum = (k: 'm1' | 'm5' | 'm15' | 'total') =>
                            queueLines.reduce((n, q) => n + (q.people?.toward?.[k] ?? 0), 0);
                          return (
                            <tr className="font-extrabold text-neutral-900 dark:text-neutral-100 bg-neutral-100/90 dark:bg-[#1c212e]">
                              <td className="py-1 px-2">All queue lines</td>
                              <td className="py-1 px-1.5 text-right">{sum('m1')}</td>
                              <td className="py-1 px-1.5 text-right">{sum('m5')}</td>
                              <td className="py-1 px-1.5 text-right">{sum('m15')}</td>
                              <td className="py-1 px-2 text-right text-orange-500">{sum('total')}</td>
                            </tr>
                          );
                        })()}
                        {totalLine && (() => {
                          const t = totalLine.people?.toward || {};
                          const lanes = (k: 'm1' | 'm5' | 'm15' | 'total') =>
                            queueLines.reduce((n, q) => n + (q.people?.toward?.[k] ?? 0), 0);
                          const diff = (k: 'm1' | 'm5' | 'm15' | 'total') => (t[k] ?? 0) - lanes(k);
                          const fmt = (n: number) => (n > 0 ? `+${n}` : `${n}`);
                          return (
                            <>
                              <tr className="font-extrabold text-sky-600 dark:text-sky-400" title="One line across all lanes: every person counted once">
                                <td className="py-1 px-2">Single total line</td>
                                <td className="py-1 px-1.5 text-right">{t.m1 ?? 0}</td>
                                <td className="py-1 px-1.5 text-right">{t.m5 ?? 0}</td>
                                <td className="py-1 px-1.5 text-right">{t.m15 ?? 0}</td>
                                <td className="py-1 px-2 text-right">{t.total ?? 0}</td>
                              </tr>
                              <tr className="text-[10px] text-neutral-500" title="Single total line minus the sum of the queue lines (0 = they agree)">
                                <td className="py-1 px-2">Difference (total − lanes)</td>
                                <td className="py-1 px-1.5 text-right">{fmt(diff('m1'))}</td>
                                <td className="py-1 px-1.5 text-right">{fmt(diff('m5'))}</td>
                                <td className="py-1 px-1.5 text-right">{fmt(diff('m15'))}</td>
                                <td className="py-1 px-2 text-right font-bold">{fmt(diff('total'))}</td>
                              </tr>
                            </>
                          );
                        })()}
                      </tbody>
                    </table>
                  ) : lineEntries.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-neutral-400 gap-1 py-8">
                      <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">No gate lines configured</span>
                      <span className="text-[9.5px] text-neutral-400 dark:text-neutral-500">No directional flow data found for this checkpoint</span>
                    </div>
                  ) : (
                    <table className="w-full text-left text-[11px] border-collapse">
                      <thead className="sticky top-0 bg-neutral-100 dark:bg-[#1f2431] text-[9.5px] font-bold text-neutral-500 uppercase border-b border-neutral-200 dark:border-[#262c3b] z-10">
                        <tr>
                          <th className="py-1 px-2">Line / direction</th>
                          <th className="py-1 px-1.5 text-right">1m</th>
                          <th className="py-1 px-1.5 text-right">5m</th>
                          <th className="py-1 px-1.5 text-right">15m</th>
                          <th className="py-1 px-2 text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-200/70 dark:divide-[#232838]">
                        {lineEntries.map((line) => {
                        const toward = line.people?.toward || {};
                        const away = line.people?.away || {};
                        const vehToward = line.vehicles?.toward || {};
                        const vehAway = line.vehicles?.away || {};

                        return (
                          <React.Fragment key={line.key}>
                            {/* Line Label Header Row */}
                            <tr className="bg-neutral-100/90 dark:bg-[#1c212e] text-neutral-900 dark:text-neutral-100 font-bold text-[10px]">
                              <td colSpan={5} className="py-1 px-2 border-y border-neutral-200 dark:border-[#262c3b] truncate" title={line.label}>
                                <div className="flex items-center justify-between">
                                  <span className="truncate">{line.label}</span>
                                  {line.camera_id && (
                                    <span className="text-[8.5px] px-1.5 py-0.2 rounded bg-orange-500/15 text-orange-500 font-bold shrink-0 ml-2">
                                      {line.camera_id}
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>

                            {/* Pedestrians Toward */}
                            <tr className="hover:bg-neutral-100/50 dark:hover:bg-[#1d222e] transition-colors">
                              <td className="py-1 px-2 text-neutral-700 dark:text-neutral-300">
                                Pedestrians <span className="text-orange-500 font-bold">↗</span> {line.toward_label}
                              </td>
                              <td className="py-1 px-1.5 text-right font-semibold text-orange-500">{toward.m1 ?? 0}</td>
                              <td className="py-1 px-1.5 text-right font-semibold text-orange-500">{toward.m5 ?? 0}</td>
                              <td className="py-1 px-1.5 text-right font-semibold text-orange-500">{toward.m15 ?? 0}</td>
                              <td className="py-1 px-2 text-right font-bold text-orange-500">{toward.total ?? 0}</td>
                            </tr>

                            {/* Pedestrians Away */}
                            <tr className="hover:bg-neutral-100/50 dark:hover:bg-[#1d222e] transition-colors">
                              <td className="py-1 px-2 text-neutral-700 dark:text-neutral-300">
                                Pedestrians <span className="text-sky-500 font-bold">↙</span> {line.away_label}
                              </td>
                              <td className="py-1 px-1.5 text-right font-semibold text-sky-500">{away.m1 ?? 0}</td>
                              <td className="py-1 px-1.5 text-right font-semibold text-sky-500">{away.m5 ?? 0}</td>
                              <td className="py-1 px-1.5 text-right font-semibold text-sky-500">{away.m15 ?? 0}</td>
                              <td className="py-1 px-2 text-right font-bold text-sky-500">{away.total ?? 0}</td>
                            </tr>

                            {/* Pedestrians Total (in/out) */}
                            <tr className="hover:bg-neutral-100/50 dark:hover:bg-[#1d222e] transition-colors font-bold text-neutral-900 dark:text-neutral-100">
                              <td className="py-1 px-2">Pedestrians total (in/out)</td>
                              <td className="py-1 px-1.5 text-right">{toward.m1 ?? 0}/{away.m1 ?? 0}</td>
                              <td className="py-1 px-1.5 text-right">{toward.m5 ?? 0}/{away.m5 ?? 0}</td>
                              <td className="py-1 px-1.5 text-right">{toward.m15 ?? 0}/{away.m15 ?? 0}</td>
                              <td className="py-1 px-2 text-right">{toward.total ?? 0}/{away.total ?? 0}</td>
                            </tr>

                            {/* Deeksha toward */}
                            {(line as any).deeksha && (
                              <tr className="hover:bg-neutral-100/50 dark:hover:bg-[#1d222e] transition-colors">
                                <td className="py-1 px-2 text-red-600 dark:text-red-400 font-semibold">
                                  Deeksha <span className="font-bold">↗</span> {line.toward_label}
                                </td>
                                <td className="py-1 px-1.5 text-right font-semibold text-red-500">{(line as any).deeksha.toward?.m1 ?? 0}</td>
                                <td className="py-1 px-1.5 text-right font-semibold text-red-500">{(line as any).deeksha.toward?.m5 ?? 0}</td>
                                <td className="py-1 px-1.5 text-right font-semibold text-red-500">{(line as any).deeksha.toward?.m15 ?? 0}</td>
                                <td className="py-1 px-2 text-right font-bold text-red-500">{(line as any).deeksha.toward?.total ?? 0}</td>
                              </tr>
                            )}

                            {/* Deeksha away */}
                            {(line as any).deeksha && (
                              <tr className="hover:bg-neutral-100/50 dark:hover:bg-[#1d222e] transition-colors">
                                <td className="py-1 px-2 text-red-600 dark:text-red-400 font-semibold">
                                  Deeksha <span className="font-bold">↙</span> {line.away_label}
                                </td>
                                <td className="py-1 px-1.5 text-right font-semibold text-red-500">{(line as any).deeksha.away?.m1 ?? 0}</td>
                                <td className="py-1 px-1.5 text-right font-semibold text-red-500">{(line as any).deeksha.away?.m5 ?? 0}</td>
                                <td className="py-1 px-1.5 text-right font-semibold text-red-500">{(line as any).deeksha.away?.m15 ?? 0}</td>
                                <td className="py-1 px-2 text-right font-bold text-red-500">{(line as any).deeksha.away?.total ?? 0}</td>
                              </tr>
                            )}

                            {/* Vehicle Classes (in/out) */}
                            {showVehicles && VEHICLE_ROWS.map((v) => {
                              const tVeh = vehToward[v.key] || {};
                              const aVeh = vehAway[v.key] || {};
                              return (
                                <tr key={v.key} className="hover:bg-neutral-100/50 dark:hover:bg-[#1d222e] transition-colors text-neutral-600 dark:text-neutral-400">
                                  <td className="py-1 px-2">{v.label}</td>
                                  <td className="py-1 px-1.5 text-right">{tVeh.m1 ?? 0}/{aVeh.m1 ?? 0}</td>
                                  <td className="py-1 px-1.5 text-right">{tVeh.m5 ?? 0}/{aVeh.m5 ?? 0}</td>
                                  <td className="py-1 px-1.5 text-right">{tVeh.m15 ?? 0}/{aVeh.m15 ?? 0}</td>
                                  <td className="py-1 px-2 text-right font-medium text-neutral-800 dark:text-neutral-200">
                                    {tVeh.total ?? 0}/{aVeh.total ?? 0}
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

              {/* 3. Recent Crossings Feed */}
              <div className="p-2 rounded-xl bg-neutral-50 dark:bg-[#181b24] border border-neutral-200 dark:border-[#242937] space-y-1 flex-1 min-h-[160px] flex flex-col overflow-hidden">
                <div className="flex items-center justify-between pb-1 border-b border-neutral-200 dark:border-[#222736] shrink-0">
                  <div className="flex items-center gap-1.5 text-[10.5px] font-bold text-neutral-800 dark:text-neutral-200">
                    <Activity className="w-3.5 h-3.5 text-orange-500" />
                    <span>Recent Crossings</span>
                    <span className="text-[9px] px-1.5 py-0.2 rounded bg-neutral-200 dark:bg-[#252b3b] text-neutral-500">
                      {filteredRecent.length}
                    </span>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setClassFilter('ALL')}
                      className={`px-1.5 py-0.2 rounded text-[8.5px] font-bold transition-colors cursor-pointer ${
                        classFilter === 'ALL'
                          ? 'bg-blue-500 text-white'
                          : 'bg-neutral-200 dark:bg-[#232837] text-neutral-500'
                      }`}
                    >
                      All
                    </button>
                    {['auto', 'person', 'two_wheeler'].map((c) => (
                      <button
                        key={c}
                        onClick={() => setClassFilter(classFilter === c ? 'ALL' : c)}
                        className={`px-1.5 py-0.2 rounded text-[8.5px] font-bold transition-colors cursor-pointer ${
                          classFilter === c
                            ? 'bg-blue-500 text-white'
                            : 'bg-neutral-200 dark:bg-[#232837] text-neutral-500'
                        }`}
                      >
                        {c === 'two_wheeler' ? '2-W' : c.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>

                {cameraLoading ? (
                  <div className="flex-1 flex flex-col items-center justify-center text-neutral-400 gap-1.5 py-6">
                    <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                    <span className="text-[10px]">Loading crossings...</span>
                  </div>
                ) : filteredRecent.length === 0 ? (
                  <div className="flex-1 flex flex-col items-center justify-center text-neutral-400 gap-1 py-6">
                    <Activity className="w-5 h-5 text-neutral-300 dark:text-neutral-600 mb-0.5" />
                    <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">No recent crossings</span>
                    <span className="text-[9.5px] text-neutral-400 dark:text-neutral-500">No detection events returned from camera API</span>
                  </div>
                ) : (
                  <div className="flex-1 overflow-y-auto space-y-1 pr-1 scrollbar-thin min-h-0">
                    {filteredRecent.map((item, idx) => {
                      const badge = getClassBadge(item.cls);
                      const isToward = item.direction === 'toward';
                      return (
                        <div 
                          key={`${item.track_id}-${item.ts}-${idx}`}
                          className="px-2 py-1 rounded-md bg-white dark:bg-[#1d212c] border border-neutral-200 dark:border-[#262c3a] flex items-center justify-between text-[10.5px]"
                        >
                          <div className="flex items-center gap-1.5 truncate">
                            <span className={`p-0.5 rounded ${badge.bg}`}>
                              {badge.icon}
                            </span>
                            <span className="font-bold text-neutral-800 dark:text-neutral-200 truncate">
                              {badge.label}
                            </span>
                            <span className="text-[9.5px] text-neutral-400">
                              #{item.track_id}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            {QUEUE_LINE_RE.test(item.line_id) && (
                              <span
                                className="px-1.5 py-0.2 rounded text-[9px] font-extrabold"
                                style={{ color: queueColor(item.line_id), background: `${queueColor(item.line_id)}22` }}
                                title={`Counted on queue line ${item.line_id}`}
                              >
                                {item.line_id}
                              </span>
                            )}
                            <span className={`px-1 py-0.2 rounded text-[9px] font-extrabold flex items-center ${
                              isToward 
                                ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' 
                                : 'bg-sky-500/10 text-sky-600 dark:text-sky-400'
                            }`}>
                              {QUEUE_LINE_RE.test(item.line_id) ? (isToward ? 'IN' : 'OUT') : (isToward ? '↑ TO' : '↓ AWAY')}
                            </span>
                            <span className="text-[9.5px] text-neutral-400">
                              {formatTimeAgo(item.ts)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>

        {/* ================= COMPACT FOOTER ================= */}
        <div className="px-3.5 py-2 bg-neutral-50 dark:bg-[#181b24] border-t border-neutral-200 dark:border-[#242937] flex items-center justify-between text-xs shrink-0">
          <div className="flex items-center gap-1.5 text-[10.5px] text-neutral-500">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
            <span>NTR District Police Command &amp; Surveillance Telemetry</span>
          </div>

          <button
            onClick={onClose}
            className="px-3.5 py-1 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold shadow-xs transition-colors cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CameraStreamModal;

